import { PageHero } from "@/components/PageHero";
import React, { useState, useEffect } from "react";
import { Link, useSearchParams } from "react-router-dom";
import SeoHead from "@/components/SeoHead";
import axios from "axios";
import { API } from "@/lib/api";
import { toast } from "sonner";
import { Heart, Send, Loader2, ArrowLeft, Check, ArrowRight, Plus, X } from "lucide-react";

/* /refer — public referral page.
 * Caregivers refer up to 5 friends at once. If they arrived via /refer?code=XXX,
 * each friend's invite will be branded with the partner org (logo, primary
 * color, tagline). No login required — rate-limited at the backend by IP.
 */
export default function Refer() {
    const [params] = useSearchParams();
    const codeFromUrl = (params.get("code") || "").trim().toUpperCase();
    const [code, setCode] = useState(codeFromUrl);
    const [brand, setBrand] = useState(null);
    const [form, setForm] = useState({
        referrer_name: "", referrer_email: "", note: "",
    });
    const [friends, setFriends] = useState([
        { id: 1, name: "", email: "" },
    ]);
    const [sending, setSending] = useState(false);
    const [sentInfo, setSentInfo] = useState(null);

    // If a code came in via URL, pull the brand pack so the page co-brands.
    useEffect(() => {
        if (!code || code.length !== 8) { setBrand(null); return; }
        let active = true;
        (async () => {
            try {
                const r = await axios.get(`${API}/org/brand/${code}`);
                if (active) setBrand(r.data);
            } catch { if (active) setBrand(null); }
        })();
        return () => { active = false; };
    }, [code]);

    const update = (k, v) => setForm(f => ({ ...f, [k]: v }));
    const updateFriend = (id, k, v) => setFriends(fs => fs.map(f => f.id === id ? { ...f, [k]: v } : f));
    const addFriend = () => {
        if (friends.length >= 5) return;
        setFriends(fs => [...fs, { id: Date.now(), name: "", email: "" }]);
    };
    const removeFriend = (id) => {
        setFriends(fs => fs.length > 1 ? fs.filter(f => f.id !== id) : fs);
    };

    const validFriends = friends.filter(
        f => f.name.trim() && /^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(f.email.trim())
    );

    const submit = async (e) => {
        e?.preventDefault?.();
        if (!form.referrer_name.trim() || !form.referrer_email.trim()) {
            toast("Please share your name and email."); return;
        }
        if (validFriends.length === 0) {
            toast("Please add at least one friend with a name + valid email."); return;
        }
        setSending(true);
        try {
            const r = await axios.post(`${API}/referrals`, {
                referrer_name: form.referrer_name,
                referrer_email: form.referrer_email,
                note: form.note,
                org_code: code,
                friends: validFriends.map(f => ({ name: f.name.trim(), email: f.email.trim() })),
            });
            setSentInfo(r.data);
            setFriends([{ id: Date.now(), name: "", email: "" }]);
            setForm(f => ({ ...f, note: "" }));
            if (r.data?.sent > 0) {
                toast.success(`Sent ${r.data.sent} of ${r.data.total} warm intros.`);
            } else {
                toast(`${r.data.total} referrals logged — Memory Lane will email them as soon as Resend's configured.`);
            }
        } catch (err) {
            const detail = err?.response?.data?.detail || "Couldn't send right now — try again in a moment.";
            toast.error(detail);
        } finally {
            setSending(false);
        }
    };

    const brandPrimary = brand?.brand?.primary_color;
    const brandName = brand?.brand?.display_name || brand?.name;
    const brandTagline = brand?.brand?.tagline;
    const brandLogo = brand?.brand?.logo_url;

    return (
        <main className="min-h-screen bg-cream">
            <SeoHead
                title="Refer a Family to Memory Lane Care"
                description="Suggest Memory Lane Care to a family caregiver who could use a personalized roadmap, GUIDE support, and a 24/7 AI navigator. Free during beta."
                canonicalPath="/refer"
            />
            <div className="max-w-2xl mx-auto px-4 sm:px-6 py-10 md:py-16">
                <Link to="/" className="inline-flex items-center gap-1.5 text-sm font-medium text-[#4C5551] hover:text-teal-dark mb-6" data-testid="refer-back">
                    <ArrowLeft size={14} strokeWidth={1.8} /> Back to Memory Lane
                </Link>

                {brand && (
                    <div
                        data-testid="refer-brand-strip"
                        className="ml-card p-4 mb-6 flex items-center gap-3 border bg-gradient-to-r from-teal-soft/40 to-yellow-light/40 border-teal-light"
                        style={brandPrimary ? { borderColor: brandPrimary + "55" } : undefined}
                    >
                        {brandLogo ? (
                            <img src={brandLogo} alt={brandName} className="w-12 h-12 rounded-xl object-cover border border-white/50" />
                        ) : (
                            <span
                                className="w-12 h-12 rounded-xl flex items-center justify-center text-white text-lg font-semibold"
                                style={{ backgroundColor: brandPrimary || "#3A6C6A" }}
                            >
                                {(brandName || "?").charAt(0).toUpperCase()}
                            </span>
                        )}
                        <div className="min-w-0">
                            <p className="text-xs uppercase tracking-wider font-semibold text-teal-dark">Refer through</p>
                            <p className="font-heading font-medium text-[#1F2624] truncate">{brandName}</p>
                            {brandTagline && <p className="text-xs italic text-[#4C5551] truncate">&ldquo;{brandTagline}&rdquo;</p>}
                        </div>
                    </div>
                )}

                <PageHero
                    testId="refer-header"
                    className="mb-6"
                    kicker="Refer a friend"
                    kickerIcon={Heart}
                    title="Know other caregivers walking the same road?"
                    subtitle={<>Caregivers learn best from caregivers. Refer up to five at once — your support group, your siblings, anyone who needs this — and we&apos;ll send each one a warm intro from you.</>}
                />

                {sentInfo && (
                    <article className="ml-card p-5 mb-6 bg-sage-light/70 border border-sage-soft" data-testid="refer-sent">
                        <div className="flex items-start gap-3">
                            <span className="w-9 h-9 rounded-2xl bg-sage text-white flex items-center justify-center shrink-0">
                                <Check size={16} strokeWidth={2.4} />
                            </span>
                            <div>
                                <p className="font-medium text-sage-dark">
                                    Thank you — {sentInfo.sent > 0 ? `${sentInfo.sent} warm intro${sentInfo.sent === 1 ? "" : "s"} on the way` : `${sentInfo.total} referral${sentInfo.total === 1 ? "" : "s"} logged`}.
                                </p>
                                <p className="text-sm text-[#4C5551] mt-1">
                                    {sentInfo.sent > 0
                                        ? "Your friends will hear from Memory Lane shortly."
                                        : "We'll email them as soon as our outbound is fully online."}{" "}You can refer more caregivers below.
                                </p>
                            </div>
                        </div>
                    </article>
                )}

                <form onSubmit={submit} className="ml-card p-5 md:p-6 bg-white border border-cream-dark space-y-4" data-testid="refer-form">
                    {/* About you */}
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                        <Field label="Your name *" value={form.referrer_name} onChange={(v) => update("referrer_name", v)} placeholder="Sarah Johnson" testid="referrer-name" autoFocus />
                        <Field label="Your email *" type="email" value={form.referrer_email} onChange={(v) => update("referrer_email", v)} placeholder="you@example.com" testid="referrer-email" />
                    </div>

                    {/* Friends list */}
                    <div className="pt-2">
                        <p className="block text-xs uppercase tracking-wider font-semibold text-[#6E7A74] mb-2">Friends to invite ({friends.length} of 5)</p>
                        <div className="space-y-2" data-testid="refer-friends">
                            {friends.map((f, i) => (
                                <div
                                    key={f.id}
                                    data-testid={`refer-friend-row-${i}`}
                                    className="grid grid-cols-1 md:grid-cols-[1fr_1fr_auto] gap-2 items-center"
                                >
                                    <input
                                        value={f.name}
                                        onChange={(e) => updateFriend(f.id, "name", e.target.value)}
                                        placeholder={`Friend #${i + 1} name`}
                                        data-testid={`refer-friend-${i}-name`}
                                        className="w-full px-3 py-2 rounded-2xl bg-cream-light border border-cream-dark text-sm focus:outline-none focus:ring-2 focus:ring-teal-soft"
                                    />
                                    <input
                                        type="email"
                                        value={f.email}
                                        onChange={(e) => updateFriend(f.id, "email", e.target.value)}
                                        placeholder={`friend${i + 1}@example.com`}
                                        data-testid={`refer-friend-${i}-email`}
                                        className="w-full px-3 py-2 rounded-2xl bg-cream-light border border-cream-dark text-sm focus:outline-none focus:ring-2 focus:ring-teal-soft"
                                    />
                                    <button
                                        type="button"
                                        onClick={() => removeFriend(f.id)}
                                        disabled={friends.length === 1}
                                        aria-label="Remove this friend"
                                        data-testid={`refer-friend-${i}-remove`}
                                        className="w-9 h-9 rounded-full bg-white border border-cream-dark text-[#6E7A74] hover:text-coral-dark hover:border-coral-softer flex items-center justify-center disabled:opacity-30 disabled:cursor-not-allowed shrink-0"
                                    >
                                        <X size={14} />
                                    </button>
                                </div>
                            ))}
                        </div>
                        {friends.length < 5 && (
                            <button
                                type="button"
                                onClick={addFriend}
                                data-testid="refer-add-friend"
                                className="mt-2 inline-flex items-center gap-1.5 text-xs font-medium text-teal-dark hover:text-teal"
                            >
                                <Plus size={12} /> Add another friend
                            </button>
                        )}
                    </div>

                    {/* Personal note */}
                    <label className="block">
                        <span className="block text-xs uppercase tracking-wider font-semibold text-[#6E7A74] mb-1">Personal note (optional — sent to all)</span>
                        <textarea
                            rows={3}
                            value={form.note}
                            onChange={(e) => update("note", e.target.value)}
                            maxLength={600}
                            placeholder="Add a sentence to make the intro yours."
                            data-testid="refer-field-note"
                            className="w-full px-3 py-2 rounded-2xl bg-cream-light border border-cream-dark text-sm focus:outline-none focus:ring-2 focus:ring-teal-soft"
                        />
                    </label>

                    {!brand && (
                        <label className="block">
                            <span className="block text-xs uppercase tracking-wider font-semibold text-[#6E7A74] mb-1">Invite code (optional)</span>
                            <input
                                value={code}
                                onChange={(e) => setCode(e.target.value.toUpperCase().slice(0, 8))}
                                placeholder="If your care provider gave you one"
                                data-testid="refer-field-code"
                                className="w-full px-3 py-2 rounded-2xl bg-cream-light border border-cream-dark text-sm font-mono focus:outline-none focus:ring-2 focus:ring-teal-soft"
                            />
                        </label>
                    )}

                    <div className="flex flex-wrap items-center gap-3 pt-1">
                        <button
                            type="submit"
                            disabled={sending || validFriends.length === 0}
                            data-testid="refer-submit"
                            className="inline-flex items-center gap-2 px-5 py-2.5 rounded-2xl bg-teal-dark text-white text-sm font-medium hover:bg-teal disabled:opacity-60"
                        >
                            {sending ? <Loader2 size={14} className="animate-spin" /> : <Send size={14} />}
                            Send {validFriends.length || ""} warm intro{validFriends.length === 1 ? "" : "s"}
                            <ArrowRight size={13} />
                        </button>
                        <p className="text-xs text-[#6E7A74]">
                            One-time emails · no signup needed · we won&apos;t add anyone to a list.
                        </p>
                    </div>
                </form>

                <p className="text-center text-xs text-[#6E7A74] mt-8">
                    Each caregiver you refer that joins us = $25 off your next Memory Lane Care Navigator session (when those go live).{" "}
                    <Link to="/pricing" className="underline">See pricing →</Link>
                </p>
            </div>
        </main>
    );
}

function Field({ label, value, onChange, type = "text", placeholder, testid, autoFocus }) {
    return (
        <label className="block">
            <span className="block text-xs uppercase tracking-wider font-semibold text-[#6E7A74] mb-1">{label}</span>
            <input
                type={type}
                value={value}
                onChange={(e) => onChange(e.target.value)}
                placeholder={placeholder}
                autoFocus={autoFocus}
                data-testid={`refer-field-${testid}`}
                className="w-full px-3 py-2 rounded-2xl bg-cream-light border border-cream-dark text-sm focus:outline-none focus:ring-2 focus:ring-teal-soft"
            />
        </label>
    );
}
